(() => {
  const purchaseRoots = new Map();
  const galleryRoots = new Map();

  const initializeGallery = (gallery) => {
    if (galleryRoots.has(gallery)) return;
    galleryRoots.set(gallery, () => {});

    customElements.whenDefined('media-gallery').then(() => {
      if (!galleryRoots.has(gallery) || !gallery.isConnected) return;
      const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      const list = viewer?.querySelector('.product__media-list');
      const previous = viewer?.querySelector('button[name="previous"]');
      const next = viewer?.querySelector('button[name="next"]');
      if (!list || !previous || !next || typeof gallery.setActiveMedia !== 'function') return;
      if (!/thumbnail|vertical/.test(gallery.dataset.desktopLayout || '')) return;

      const controls = document.createElement('div');
      controls.className = 'zl-product-gallery-controls';
      controls.hidden = true;
      [previous, next].forEach((source, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'zl-product-gallery-arrow';
        button.dataset.direction = index ? '1' : '-1';
        button.setAttribute('aria-label', source.getAttribute('aria-label'));
        button.setAttribute('aria-controls', list.id);
        button.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m14 6-6 6 6 6"></path></svg>';
        controls.append(button);
      });
      viewer.append(controls);

      let frame = 0;
      const slides = () => [...list.children].filter((slide) =>
        slide.matches('li[data-media-id]') && !slide.hidden &&
        (!slide.classList.contains('product__media-item--variant') || slide === list.firstElementChild));
      const update = () => {
        frame = 0;
        controls.hidden = slides().length < 2;
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      const onSlide = (event) => {
        const mediaId = event.detail?.currentElement?.dataset.mediaId;
        if (!mediaId || typeof gallery.setActiveThumbnail !== 'function') return;
        const thumbnail = [...gallery.querySelectorAll('[id^="GalleryThumbnails-"] [data-target]')]
          .find((item) => item.dataset.target === mediaId);
        if (thumbnail) gallery.setActiveThumbnail(thumbnail);
      };
      const navigate = (event) => {
        const button = event.target.closest('[data-direction]');
        if (!button || !controls.contains(button)) return;
        const items = slides();
        if (items.length < 2) return;
        event.preventDefault();
        const selected = gallery.querySelector('.thumbnail[aria-current="true"]')?.closest('[data-target]')?.dataset.target;
        const active = Math.max(0, items.findIndex((slide) => slide.dataset.mediaId === selected || (!selected && slide.classList.contains('is-active'))));
        const target = items[(active + Number(button.dataset.direction) + items.length) % items.length];
        gallery.setActiveMedia(target.dataset.mediaId, false);
      };
      const mutations = new MutationObserver(schedule);
      mutations.observe(list, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden']});
      controls.addEventListener('click', navigate);
      viewer.addEventListener('slideChanged', onSlide);
      galleryRoots.set(gallery, () => {
        mutations.disconnect();
        cancelAnimationFrame(frame);
        controls.removeEventListener('click', navigate);
        viewer.removeEventListener('slideChanged', onSlide);
        controls.remove();
      });
      update();
    });
  };

  const initializePurchase = (root) => {
    if (purchaseRoots.has(root)) return;
    if (!root.querySelector('.zl-product-info')) return;
    let frame = 0;

    const isVisible = (element) => {
      if (!element.getClientRects().length) return false;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      for (let node = element; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (node.hidden || node.getAttribute('aria-hidden') === 'true' ||
            style.display === 'none' || style.visibility === 'hidden' ||
            style.visibility === 'collapse' || style.opacity === '0') return false;
      }
      return true;
    };

    const update = () => {
      frame = 0;
      const widgets = [...root.querySelectorAll('.zl-product-info #xb-quantity-breaks-root .xb_quantity_breaks')];
      const active = widgets.some((widget) => {
        if (!isVisible(widget)) return false;
        const options = [...widget.querySelectorAll('.xb_quantity_list input[type="radio"]')].filter((input) =>
          !input.matches(':disabled') && isVisible(input.closest('label') || input.parentElement));
        return options.length >= 2 && options.filter((input) => input.checked).length === 1;
      });
      const state = String(active);
      if (root.dataset.zlXboostActive !== state) root.dataset.zlXboostActive = state;
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    const mutations = new MutationObserver(schedule);
    mutations.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'disabled'],
    });
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    resize?.observe(root);
    root.addEventListener('change', schedule);
    window.addEventListener('resize', schedule, {passive: true});
    purchaseRoots.set(root, () => {
      mutations.disconnect();
      resize?.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      root.removeEventListener('change', schedule);
      delete root.dataset.zlXboostActive;
    });
    update();
  };

  const initializeTabs = (root) => {
    if (root.dataset.zlProductReady === 'true') return;

    const tabs = [...root.querySelectorAll('[data-zl-product-tab]')];
    const panels = [...root.querySelectorAll('[data-zl-product-panel]')];

    const activate = (tab) => {
      const target = tab.dataset.zlProductTab;

      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('zl-product-is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.zlProductPanel !== target;
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        activate(tabs[nextIndex]);
        tabs[nextIndex].focus();
      });
    });

    root.dataset.zlProductReady = 'true';
  };

  const initializeModal = (trigger) => {
    if (trigger.dataset.zlProductModalReady === 'true') return;

    const dialogId = trigger.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    const image = dialog?.querySelector('[data-zl-product-modal-image], .zl-product-modal-image');
    const stage = dialog?.querySelector('[data-zl-product-modal-stage]');
    const zoomValue = dialog?.querySelector('[data-zl-product-modal-zoom-value]');
    const closeButtons = dialog ? [...dialog.querySelectorAll('[data-zl-product-modal-close]')] : [];
    const zoomButtons = dialog ? [...dialog.querySelectorAll('[data-zl-product-modal-zoom]')] : [];
    if (!trigger || !dialog || !image || !stage || !zoomValue) return;

    let scale = 1;
    let lastFocused = null;

    const setScale = (nextScale) => {
      scale = Math.min(3, Math.max(0.5, nextScale));
      image.style.width = `${scale * 100}%`;
      zoomValue.textContent = `${Math.round(scale * 100)}%`;
      if (scale === 1) {
        stage.scrollTo({ top: 0, left: 0 });
      }
    };

    const close = () => {
      dialog.hidden = true;
      document.body.classList.remove('zl-product-modal-open');
      setScale(1);
      if (lastFocused) lastFocused.focus();
    };

    const open = () => {
      lastFocused = document.activeElement;
      dialog.hidden = false;
      document.body.classList.add('zl-product-modal-open');
      setScale(1);
      dialog.querySelector('.zl-product-modal-close').focus();
    };

    trigger.addEventListener('click', open);
    closeButtons.forEach((button) => button.addEventListener('click', close));
    zoomButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const action = button.dataset.zlProductModalZoom;
        if (action === 'in') setScale(scale + 0.25);
        if (action === 'out') setScale(scale - 0.25);
        if (action === 'reset') setScale(1);
      });
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    trigger.dataset.zlProductModalReady = 'true';
  };

  const initializeFaq = (root) => {
    if (root.dataset.zlProductFaqReady === 'true') return;

    const items = [...root.querySelectorAll('.zl-product-faq-item')];
    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((otherItem) => {
          if (otherItem !== item) otherItem.open = false;
        });
      });
    });

    root.dataset.zlProductFaqReady = 'true';
  };

  const initialize = (scope = document) => {
    if (scope.matches?.('.zl-product-section')) initializePurchase(scope);
    scope.querySelectorAll('.zl-product-section').forEach(initializePurchase);
    scope.querySelectorAll('.zl-product-section .zl-product-gallery media-gallery').forEach(initializeGallery);
    scope.querySelectorAll('[data-zl-product-tabs]').forEach(initializeTabs);
    scope.querySelectorAll('[data-zl-product-modal-open]').forEach(initializeModal);
    scope.querySelectorAll('[data-zl-product-faq]').forEach(initializeFaq);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    galleryRoots.forEach((dispose, gallery) => {
      if (event.target === gallery || event.target.contains(gallery)) {
        dispose();
        galleryRoots.delete(gallery);
      }
    });
    purchaseRoots.forEach((dispose, root) => {
      if (event.target === root || event.target.contains(root)) {
        dispose();
        purchaseRoots.delete(root);
      }
    });
  });
})();
